// src/components/listing/specialized/VehiclesForm.jsx
import React from 'react';
import { FiCheck } from 'react-icons/fi';
import Input from '../../../common/Input';
import Dropdown from '../../../common/Dropdown';

const VehiclesForm = ({ form, errors, submitting, onFieldChange, categoryId }) => {
  
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 30 }, (_, i) => ({
    id: currentYear - i,
    name: String(currentYear - i),
    value: String(currentYear - i)
  }));

  // Determine vehicle type based on category ID
  const getVehicleType = () => {
    if (!categoryId) return 'cars';
    
    const categoryNum = parseInt(categoryId);
    switch(categoryNum) {
      case 24: return 'cars'; // Cars
      case 25: return 'motorcycles'; // Motorcycles & Scooters
      case 26: return 'trucks'; // Trucks & Trailers
      case 27: return 'vans'; // Buses & Vans
      case 30: return 'boats'; // Boats & Watercraft
      default: return 'cars';
    }
  };

  const vehicleType = getVehicleType();
  // console.log(`🚗 Vehicle type detected: ${vehicleType} for category ${categoryId}`);

  const renderSimpleItem = (item) => (
    <div className="p-3 hover:bg-gray-50 cursor-pointer">
      <span className="text-gray-900">{item.name || item.label}</span>
    </div>
  );

  // Common fields for all vehicle types
  const renderCommonFields = () => (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Make *
          </label>
          <Input
            type="text"
            placeholder={vehicleType === 'motorcycles' ? "e.g., Yamaha, Honda" : "e.g., Toyota, Ford"}
            value={form.make || ''}
            onChange={(e) => onFieldChange('make', e.target.value)}
            error={errors.make}
            disabled={submitting}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Model *
          </label>
          <Input
            type="text"
            placeholder={vehicleType === 'motorcycles' ? "e.g., R15, CBR" : "e.g., Camry, F-150"}
            value={form.model || ''}  // Change from form.model
            onChange={(e) => onFieldChange('vehicle_model', e.target.value)}  // Change from 'model'
            error={errors.model}  // Change from errors.model
            disabled={submitting}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Year *
          </label>
          <Dropdown
            value={form.year || ''}
            onValueChange={(value) => onFieldChange('year', value)}
            items={yearOptions}
            placeholder="Select year"
            error={errors.year}
            disabled={submitting}
            renderItem={renderSimpleItem}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Color *
          </label>
          <Input
            type="text"
            placeholder="e.g., Red, Blue, Black"
            value={form.color || ''}  // Change from form.color
            onChange={(e) => onFieldChange('vehicle_color', e.target.value)}  // Change from 'color'
            error={errors.color}  // Change from errors.color
            disabled={submitting}
          />
        </div>
      </div>

      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Condition *
        </label>
        <Dropdown
          value={form.condition || 'good'}
          onValueChange={(value) => onFieldChange('condition', value)}
          items={[
            { id: 'excellent', name: 'Excellent', value: 'excellent' },
            { id: 'good', name: 'Good', value: 'good' },
            { id: 'fair', name: 'Fair', value: 'fair' },
            { id: 'needs_repair', name: 'Needs Repair', value: 'needs_repair' },
          ]}
          disabled={submitting}
          renderItem={renderSimpleItem}
        />
      </div>
    </>
  );

  // Fields specific to Cars, SUVs, etc.
  const renderCarFields = () => (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Body Type
          </label>
          <Dropdown
            value={form.body_type || ''}
            onValueChange={(value) => onFieldChange('body_type', value)}
            items={[
              { id: 'sedan', name: 'Sedan', value: 'sedan' },
              { id: 'suv', name: 'SUV', value: 'suv' },
              { id: 'hatchback', name: 'Hatchback', value: 'hatchback' },
              { id: 'coupe', name: 'Coupe', value: 'coupe' },
              { id: 'convertible', name: 'Convertible', value: 'convertible' },
              { id: 'wagon', name: 'Wagon', value: 'wagon' },
            ]}
            disabled={submitting}
            renderItem={renderSimpleItem}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Transmission
          </label>
          <Dropdown
            value={form.transmission || ''}
            onValueChange={(value) => onFieldChange('transmission', value)}
            items={[
              { id: 'automatic', name: 'Automatic', value: 'automatic' },
              { id: 'manual', name: 'Manual', value: 'manual' },
              { id: 'cvt', name: 'CVT', value: 'cvt' },
            ]}
            disabled={submitting}
            renderItem={renderSimpleItem}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Engine Size
          </label>
          <Input
            type="text"
            placeholder="e.g., 2.0L, 2000cc"
            value={form.engine_size || ''}
            onChange={(e) => onFieldChange('engine_size', e.target.value)}
            disabled={submitting}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Fuel Type
          </label>
          <Dropdown
            value={form.fuel_type || ''}
            onValueChange={(value) => onFieldChange('fuel_type', value)}
            items={[
              { id: 'petrol', name: 'Petrol', value: 'petrol' },
              { id: 'diesel', name: 'Diesel', value: 'diesel' },
              { id: 'hybrid', name: 'Hybrid', value: 'hybrid' },
              { id: 'electric', name: 'Electric', value: 'electric' },
            ]}
            disabled={submitting}
            renderItem={renderSimpleItem}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Doors
          </label>
          <Input
            type="number"
            placeholder="e.g., 4"
            value={form.doors || ''}
            onChange={(e) => onFieldChange('doors', e.target.value)}
            disabled={submitting}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Seats
          </label>
          <Input
            type="number"
            placeholder="e.g., 5"
            value={form.seats || ''}
            onChange={(e) => onFieldChange('seats', e.target.value)}
            disabled={submitting}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Mileage (km)
          </label>
          <Input
            type="number"
            placeholder="e.g., 50000"
            value={form.mileage || ''}
            onChange={(e) => onFieldChange('mileage', e.target.value)}
            disabled={submitting}
          />
        </div>
      </div>

      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Interior Color
        </label>
        <Input
          type="text"
          placeholder="e.g., Beige, Black, Brown"
          value={form.interior_color || ''}
          onChange={(e) => onFieldChange('interior_color', e.target.value)}
          disabled={submitting}
        />
      </div>
    </>
  );

  // Fields specific to Motorcycles
  const renderMotorcycleFields = () => (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Motorcycle Type
          </label>
          <Dropdown
            value={form.vehicle_type || ''}
            onValueChange={(value) => onFieldChange('vehicle_type', value)}
            items={[
              { id: 'sport', name: 'Sport Bike', value: 'sport' },
              { id: 'cruiser', name: 'Cruiser', value: 'cruiser' },
              { id: 'scooter', name: 'Scooter', value: 'scooter' },
              { id: 'naked', name: 'Naked Bike', value: 'naked' },
              { id: 'adventure', name: 'Adventure', value: 'adventure' },
              { id: 'tour', name: 'Touring', value: 'tour' },
            ]}
            disabled={submitting}
            renderItem={renderSimpleItem}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Engine CC
          </label>
          <Input
            type="number"
            placeholder="e.g., 150, 1000"
            value={form.engine_cc || ''}
            onChange={(e) => onFieldChange('engine_cc', e.target.value)}
            disabled={submitting}
          />
        </div>
      </div>

      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Mileage (km)
        </label>
        <Input
          type="number"
          placeholder="e.g., 15000"
          value={form.mileage || ''}
          onChange={(e) => onFieldChange('mileage', e.target.value)}
          disabled={submitting}
        />
      </div>
    </>
  );

  // Fields specific to Boats
  const renderBoatFields = () => (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Boat Type
          </label>
          <Dropdown
            value={form.boat_type || ''}
            onValueChange={(value) => onFieldChange('boat_type', value)}
            items={[
              { id: 'speedboat', name: 'Speedboat', value: 'speedboat' },
              { id: 'yacht', name: 'Yacht', value: 'yacht' },
              { id: 'sailboat', name: 'Sailboat', value: 'sailboat' },
              { id: 'fishing', name: 'Fishing Boat', value: 'fishing' },
              { id: 'pontoon', name: 'Pontoon Boat', value: 'pontoon' },
              { id: 'jetski', name: 'Jet Ski', value: 'jetski' },
            ]}
            disabled={submitting}
            renderItem={renderSimpleItem}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Length (meters)
          </label>
          <Input
            type="number"
            placeholder="e.g., 5.5"
            value={form.length || ''}
            onChange={(e) => onFieldChange('length', e.target.value)}
            disabled={submitting}
          />
        </div>
      </div>
    </>
  );

  // Common status fields for all types
  const renderStatusFields = () => (
    <div className="mt-6 pt-6 border-t border-gray-200">
      <h5 className="text-md font-semibold text-gray-900 mb-4">Vehicle Status</h5>
      
      <div className="space-y-3">
        <label className="flex items-center space-x-3 cursor-pointer">
          <input
            type="checkbox"
            checked={form.is_registered || false}
            onChange={(e) => onFieldChange('is_registered', e.target.checked)}
            disabled={submitting}
            className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
          />
          <span className="text-gray-700">Vehicle is registered</span>
        </label>

        <label className="flex items-center space-x-3 cursor-pointer">
          <input
            type="checkbox"
            checked={form.custom_duty_paid || true}
            onChange={(e) => onFieldChange('custom_duty_paid', e.target.checked)}
            disabled={submitting}
            className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
          />
          <span className="text-gray-700">Custom duty paid</span>
        </label>
      </div>
    </div>
  );

  return (
    <div>
      <h4 className="text-lg font-semibold text-gray-900 mb-4">
        {vehicleType === 'motorcycles' ? 'Motorcycle Details' : 
         vehicleType === 'boats' ? 'Boat Details' :
         vehicleType === 'trucks' ? 'Truck Details' :
         vehicleType === 'vans' ? 'Van Details' : 'Vehicle Details'}
      </h4>
      
      {/* Common fields for all vehicle types */}
      {renderCommonFields()}

      {/* Type-specific fields */}
      {vehicleType === 'cars' && renderCarFields()}
      {vehicleType === 'motorcycles' && renderMotorcycleFields()}
      {vehicleType === 'boats' && renderBoatFields()}
      {vehicleType === 'trucks' && renderCarFields()} {/* Trucks can use car fields for now */}
      {vehicleType === 'vans' && renderCarFields()} {/* Vans can use car fields */}

      {/* Common status fields */}
      {renderStatusFields()}
    </div>
  );
};

export default VehiclesForm;